import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GenericChatService, ChatMessage, ChatConfig, ChatSession, ToolCall } from '../../services/genericChatService';
import { aiCharacterService, AICharacter } from '../../services/aiCharacterService';
import PageHeader from '../common/PageHeader';
import ChatSessionList from './ChatSessionList';
import './GenericChat.css';

interface GenericChatProps {
  onToggleSidebar: () => void;
}

const ToolCallModal: React.FC<{ toolCall: ToolCall; onClose: () => void }> = ({ toolCall, onClose }) => {
  return (
    <div className="tool-call-modal-overlay" onClick={onClose}>
      <div className="tool-call-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="tool-call-modal-header">
          <h4>
            <span className="tool-call-icon">🛠️</span> Tool Call: {toolCall.name}
          </h4>
          <button onClick={onClose} className="tool-call-modal-close-btn">✕</button>
        </div>
        <div className="tool-call-modal-body">
          <div className="tool-call-section">
            <h5 className="tool-call-toggle">Input</h5>
            <pre className="tool-call-json">{JSON.stringify(toolCall.input, null, 2)}</pre>
          </div>
          {toolCall.result && (
            <div className="tool-call-section">
              <h5 className={`tool-call-toggle ${toolCall.isError ? 'error' : ''}`}>Result</h5>
              <pre className={`tool-call-json ${toolCall.isError ? 'error' : ''}`}>{JSON.stringify(toolCall.result, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GenericChat: React.FC<GenericChatProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [characters, setCharacters] = useState<AICharacter[]>([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [chatConfig, setChatConfig] = useState<ChatConfig>({
    provider: 'claude',
    model: 'claude-haiku-4-5'
  });
  const [selectedCharacter, setSelectedCharacter] = useState<AICharacter | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedToolCall, setSelectedToolCall] = useState<ToolCall | null>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Load AI characters and sessions when component mounts
  useEffect(() => {
    if (user) {
      loadCharacters();
      loadSessions();
    }
  }, [user]);

  // Load chat history when session changes
  useEffect(() => {
    if (user && currentSessionId) {
      loadSession(currentSessionId);
    } else if (user && !currentSessionId) {
      // No session selected, show welcome message
      initializeWelcomeMessage();
    }
  }, [currentSessionId, user]);

  const loadCharacters = async () => {
    if (!user) return;
    
    setLoadingCharacters(true);
    try {
      const response = await aiCharacterService.getUserCharacters({
        user_id: user.id.toString(),
        is_active: true
      });
      if (response.success && response.characters) {
        setCharacters(response.characters);
      }
    } catch (error) {
      console.error('Error loading AI characters:', error);
    } finally {
      setLoadingCharacters(false);
    }
  };

  const sessionMatchesConfig = (session: ChatSession, config: ChatConfig): boolean => {
    // Match by character_id if config has characterId
    if (config.characterId) {
      return session.character_id === config.characterId;
    }
    
    // Match by provider and model if config has provider and model
    if (config.provider && config.model) {
      return session.provider === config.provider && session.model === config.model;
    }
    
    return false;
  };

  const loadSessions = async () => {
    if (!user) return;
    
    setLoadingSessions(true);
    try {
      const userSessions = await GenericChatService.loadSessions(user.id);
      setSessions(userSessions);
      
      // Auto-load empty session if no session is currently selected
      if (!currentSessionId && chatConfig) {
        // Ensure characters are loaded if we're matching by character
        if (chatConfig.characterId && characters.length === 0) {
          await loadCharacters();
        }
        
        // Find the most recent session matching the current config
        const matchingSession = userSessions.find(session => sessionMatchesConfig(session, chatConfig));
        
        if (matchingSession) {
          // Load the session to check if it's empty
          try {
            const { messages: sessionMessages } = await GenericChatService.loadSession(matchingSession.id);
            
            // If the session is empty, automatically load it
            if (sessionMessages.length === 0) {
              await loadSession(matchingSession.id);
            }
          } catch (error) {
            console.error('Error checking empty session:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId: number) => {
    if (!user) return;
    
    try {
      const { session, messages: sessionMessages } = await GenericChatService.loadSession(sessionId);
      
      // Update chat config based on session
      if (session.character_id) {
        // Ensure characters are loaded
        if (characters.length === 0) {
          await loadCharacters();
        }
        const character = characters.find(c => c.id === session.character_id);
        if (character) {
          setSelectedCharacter(character);
          setChatConfig({ characterId: character.id });
        } else {
          // Character not found, use default
          setSelectedCharacter(null);
          setChatConfig({
            provider: 'claude',
            model: 'claude-haiku-4-5'
          });
        }
      } else if (session.provider && session.model) {
        setSelectedCharacter(null);
        setChatConfig({
          provider: session.provider,
          model: session.model
        });
      }
      
      setMessages(sessionMessages);
      setCurrentSessionId(sessionId);
      
      // If no messages, show welcome message
      if (sessionMessages.length === 0) {
        initializeWelcomeMessage();
      }
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const createNewSession = async () => {
    if (!user || !chatConfig) return;
    
    try {
      const session = await GenericChatService.createSession(chatConfig);
      setCurrentSessionId(session.id);
      setMessages([]);
      await loadSessions();
      initializeWelcomeMessage();
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  const initializeWelcomeMessage = async (config?: ChatConfig) => {
    const configToUse = config || chatConfig;
    if (!user || !configToUse) return;
    
    try {
      const userName = user.display_name || user.username;
      const welcomeText = await GenericChatService.generateWelcomeMessage(configToUse, userName);
      const welcomeMessage = GenericChatService.createMessage(welcomeText, 'assistant');
      setMessages([welcomeMessage]);
      // Don't save welcome message to database
    } catch (error) {
      console.error('Error initializing welcome message:', error);
    }
  };

  const handleSessionDeleted = (deletedSessionId: number) => {
    // If the deleted session was the current one, clear it
    if (currentSessionId === deletedSessionId) {
      setCurrentSessionId(null);
      setMessages([]);
      initializeWelcomeMessage();
    }
  };

  const handleSessionSelect = async (sessionId: number) => {
    await loadSession(sessionId);
    // Close the sidebar after selecting a session
    setShowHistorySidebar(false);
  };

  const handleConfigChange = async (newConfig: ChatConfig, oldConfig?: ChatConfig) => {
    const configToUseForSave = oldConfig || chatConfig;
    
    // Save current session if it exists
    if (currentSessionId && messages.length > 0) {
      try {
        // Filter out welcome messages before saving
        const messagesToSave = messages.filter(msg => msg.role !== 'assistant' || !msg.content.includes('Hello'));
        if (messagesToSave.length > 0) {
          await GenericChatService.saveChatHistory(configToUseForSave, messagesToSave, currentSessionId);
        }
      } catch (error) {
        console.error('Error saving session:', error);
      }
    }

    // Create new session for new config
    try {
      const session = await GenericChatService.createSession(newConfig);
      setCurrentSessionId(session.id);
      setChatConfig(newConfig);
      setMessages([]);
      await loadSessions();
      setTimeout(() => {
        initializeWelcomeMessage(newConfig);
      }, 100);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleCharacterSelect = async (value: string) => {
    // Capture old config before updating state
    const oldConfig = { ...chatConfig };
    
    if (value === 'default') {
      // Switch to default Claude
      const newConfig: ChatConfig = {
        provider: 'claude',
        model: 'claude-haiku-4-5'
      };
      // Update state immediately for UI responsiveness
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else if (value === 'default-gemini') {
      // Switch to default Gemini
      const newConfig: ChatConfig = {
        provider: 'gemini',
        model: 'gemini-2.5-pro'
      };
      // Update state immediately for UI responsiveness
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else if (value === 'default-grok') {
      // Switch to default Grok
      const newConfig: ChatConfig = {
        provider: 'grok',
        model: 'grok-4-1-fast-non-reasoning-latest'
      };
      // Update state immediately for UI responsiveness
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else {
      // Find character
      const characterId = parseInt(value);
      const character = characters.find(c => c.id === characterId);
      if (character) {
        const newConfig: ChatConfig = {
          characterId: character.id
        };
        // Update state immediately for UI responsiveness
        setSelectedCharacter(character);
        setChatConfig(newConfig);
        await handleConfigChange(newConfig, oldConfig);
      }
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !chatConfig || !user) return;

    // Create session if it doesn't exist
    let sessionId = currentSessionId;
    if (!sessionId) {
      try {
        const session = await GenericChatService.createSession(chatConfig);
        sessionId = session.id;
        setCurrentSessionId(sessionId);
        await loadSessions();
      } catch (error) {
        console.error('Error creating session:', error);
        return;
      }
    }

    const userMessage = GenericChatService.createMessage(inputMessage, 'user');
    const currentMessages = [...messages, userMessage];
    
    setMessages(currentMessages);
    setInputMessage('');
    setIsTyping(true);
    
    // Save user message to session
    try {
      await GenericChatService.saveMessage(sessionId, userMessage);
    } catch (error) {
      console.error('Error saving user message:', error);
    }

    try {
      // Filter out welcome messages for AI context
      const contextMessages = messages.filter(msg => msg.role !== 'assistant' || !msg.content.includes('Hello'));
      
      const response = await GenericChatService.sendMessage(
        inputMessage,
        chatConfig,
        contextMessages,
        user.id // Pass user ID for tool execution
      );

      const aiMessage = GenericChatService.createMessage(
        response.response,
        'assistant',
        response.images,
        response.toolCalls
      );
      
      const newMessages = [...currentMessages, aiMessage];
      setMessages(newMessages);
      
      // Save AI message to session
      try {
        await GenericChatService.saveMessage(sessionId, aiMessage);
      } catch (error) {
        console.error('Error saving AI message:', error);
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = GenericChatService.createMessage(
        'I apologize, but I encountered an error. Please try again.',
        'assistant'
      );
      const newMessages = [...currentMessages, errorMessage];
      setMessages(newMessages);
      
      // Save error message to session
      try {
        await GenericChatService.saveMessage(sessionId, errorMessage);
      } catch (error) {
        console.error('Error saving error message:', error);
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetConversation = async () => {
    if (!chatConfig || !user) return;
    
    setMessages([]);
    setCurrentSessionId(null);
    setShowResetConfirm(false);
    
    // Create new session
    try {
      await createNewSession();
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  const getCurrentConfigInfo = () => {
    if (selectedCharacter) {
      return {
        name: selectedCharacter.name,
        provider: selectedCharacter.model_provider,
        model: selectedCharacter.model_name,
        personaInfo: selectedCharacter.persona_info
      };
    }
    const provider = chatConfig.provider || 'claude';
    const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
    return {
      name: `${providerName} AI`,
      provider: provider,
      model: chatConfig.model || 'claude-haiku-4-5',
      personaInfo: null
    };
  };

  // Show authentication prompt if no user
  if (!user) {
    return (
      <div className="generic-chat-container">
        <div className="generic-chat-auth-prompt">
          <div className="auth-prompt-content">
            <h2>🤖 AI Chat</h2>
            <p>Sign in to start chatting with AI assistants. You can use default AI models or create custom AI characters!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="generic-chat-container generic-chat-fullscreen"
      style={{ marginLeft: showHistorySidebar ? '300px' : '0' }}
    >
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-title">/chat</h1>}
        rightActions={
          <div className="generic-chat-menu-container" ref={menuRef}>
            <button 
              className="generic-chat-menu-btn" 
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋯
            </button>
            {showMenu && (
              <div className="generic-chat-menu-dropdown">
                <button 
                  className="generic-chat-menu-item" 
                  onClick={() => {
                    setShowHistorySidebar(!showHistorySidebar);
                    setShowMenu(false);
                  }}
                >
                  <span className="generic-chat-menu-icon">📜</span>
                  <span className="generic-chat-menu-text">Toggle History</span>
                </button>
                <button 
                  className="generic-chat-menu-item" 
                  onClick={() => {
                    setShowContextPanel(!showContextPanel);
                    setShowMenu(false);
                  }}
                >
                  <span className="generic-chat-menu-icon">📋</span>
                  <span className="generic-chat-menu-text">Toggle Context</span>
                </button>
                <button 
                  className="generic-chat-menu-item" 
                  onClick={() => {
                    setShowResetConfirm(true);
                    setShowMenu(false);
                  }}
                >
                  <span className="generic-chat-menu-icon">🔄</span>
                  <span className="generic-chat-menu-text">Reset Conversation</span>
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* History Sidebar */}
      {showHistorySidebar && (
        <div className="generic-chat-history-sidebar">
          <div className="generic-chat-history-sidebar-header">
            <h4>Chat History</h4>
            <button 
              className="generic-chat-history-close-btn"
              onClick={() => setShowHistorySidebar(false)}
              title="Close history"
            >
              ✕
            </button>
          </div>
          <div className="generic-chat-history-sidebar-content">
            <button 
              className="generic-chat-new-chat-btn"
              onClick={createNewSession}
              disabled={isTyping}
            >
              + New Chat
            </button>
            <ChatSessionList
              sessions={sessions}
              currentSessionId={currentSessionId}
              loadingSessions={loadingSessions}
              onSessionSelect={handleSessionSelect}
              onSessionDeleted={handleSessionDeleted}
              onSessionsUpdated={loadSessions}
              characters={characters}
            />
          </div>
        </div>
      )}

      {/* Character/Provider Selector */}
      <div className="generic-chat-selector">
        <select
          className="generic-chat-selector-dropdown"
          value={selectedCharacter ? selectedCharacter.id.toString() : (chatConfig.provider === 'gemini' ? 'default-gemini' : chatConfig.provider === 'grok' ? 'default-grok' : 'default')}
          onChange={(e) => handleCharacterSelect(e.target.value)}
          disabled={isTyping || loadingCharacters}
        >
          <option value="default">Default: Claude</option>
          <option value="default-gemini">Default: Gemini</option>
          <option value="default-grok">Default: Grok</option>
          {characters.map(character => (
            <option key={character.id} value={character.id}>
              {character.name} ({character.model_provider})
            </option>
          ))}
        </select>
        {selectedCharacter && (
          <button
            className="generic-chat-edit-character-btn"
            onClick={() => navigate(`/ai-characters/${selectedCharacter.id}/edit`)}
            disabled={isTyping}
            title="Edit character"
          >
            ✏️
          </button>
        )}
      </div>

      {/* Context Panel */}
      {showContextPanel && (
        <div className="generic-chat-context-panel">
          <div className="generic-chat-context-panel-header">
            <h4>📋 AI Context</h4>
            <span className="generic-chat-context-panel-subtitle">Current configuration</span>
          </div>
          <div className="generic-chat-context-panel-content">
            {(() => {
              const configInfo = getCurrentConfigInfo();
              return (
                <>
                  <div className="generic-chat-context-section">
                    <h5>🤖 AI Assistant</h5>
                    <div className="generic-chat-context-item">
                      <span className="generic-chat-context-label">Name:</span>
                      <span className="generic-chat-context-value">{configInfo.name}</span>
                    </div>
                    <div className="generic-chat-context-item">
                      <span className="generic-chat-context-label">Provider:</span>
                      <span className="generic-chat-context-value">{configInfo.provider}</span>
                    </div>
                    <div className="generic-chat-context-item">
                      <span className="generic-chat-context-label">Model:</span>
                      <span className="generic-chat-context-value">{configInfo.model}</span>
                    </div>
                    {configInfo.personaInfo && (
                      <div className="generic-chat-context-item">
                        <span className="generic-chat-context-label">Persona:</span>
                        <div className="generic-chat-context-persona">
                          {typeof configInfo.personaInfo === 'object' 
                            ? JSON.stringify(configInfo.personaInfo, null, 2)
                            : String(configInfo.personaInfo)
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className="generic-chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`generic-chat-message ${message.role}`}>
            <div className="generic-chat-message-content">
              {message.toolCalls && message.toolCalls.length > 0 && (
                <div className="tool-calls-section">
                  {message.toolCalls.map((toolCall, index) => (
                    <button key={index} className="tool-call-summary-button" onClick={() => setSelectedToolCall(toolCall)}>
                      <span className="tool-call-icon">🛠️</span>
                      <span className="tool-call-name">Used tool: {toolCall.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="generic-chat-message-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
              </div>
              {message.images && message.images.length > 0 && (
                <div className="generic-chat-message-images">
                  {message.images.map((image, index) => (
                    <div key={index} className="generic-chat-image-container">
                      <img
                        src={`data:${image.mimeType};base64,${image.data}`}
                        alt={image.alt || 'Generated illustration'}
                        className="generic-chat-image"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Failed to load generated image:', e);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      {image.alt && (
                        <div className="generic-chat-image-caption">{image.alt}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="generic-chat-message-time">
              {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="generic-chat-message assistant typing">
            <div className="generic-chat-message-content">
              <div className="generic-chat-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      <div className="generic-chat-input-area">
        <div className="generic-chat-input-container">
          <input
            ref={chatInputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="generic-chat-input"
            disabled={isTyping}
          />
          <button 
            onClick={sendMessage} 
            disabled={!inputMessage.trim() || isTyping}
            className="generic-chat-send-btn"
          >
            ➤
          </button>
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="generic-chat-reset-confirm-overlay">
          <div className="generic-chat-reset-confirm-dialog">
            <h4>Reset Conversation?</h4>
            <p>This will clear all messages and start a new conversation. This action cannot be undone.</p>
            <div className="generic-chat-reset-confirm-buttons">
              <button 
                className="generic-chat-reset-cancel-btn" 
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="generic-chat-reset-confirm-btn" 
                onClick={resetConversation}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tool Call Modal */}
      {selectedToolCall && (
        <ToolCallModal toolCall={selectedToolCall} onClose={() => setSelectedToolCall(null)} />
      )}
    </div>
  );
};

export default GenericChat;

